import { useEffect } from 'react';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { BASE_PATH, isDev, isProduction } from '@utils/Environment';
import { googleManager } from '@utils/GoogleTagManager';
import GeneralSearchTalent from '@company/components/GeneralSearchTalent/GeneralSearchTalent';
import PayrollServices from '@company/pages/JobOverview/PayrollServices/PayrollServices';
import { UpdateWorkerProvider } from '@company/state/useUpdateWorker/useUpdateWorker';
import { JobWorkerShiftProvider } from '@company/state/jobWorkerShiftContext';
import LeaveSingleReview from '@company/pages/LeaveReview/LeaveSingleReview';
import { JobAssignmentProvider } from '@company/state/jobAssignmentContext';
import Dashboard from '@company/pages/Dashboard';
import JobOverview from '@company/pages/JobOverview/JobOverview';
import WorkerProfilePage from '@company/pages/WorkerProfile/WorkerProfilePage';
import CommunicationHub from '@company/pages/CommunicationHub/CommunicationHub';
import { useCompanyContext } from '@company/state/companyContext';
import { JobShiftProvider } from '@company/state/jobShiftContext';
import { ActivityProvider } from '@company/state/activityContext';
import { LeaveReview } from '@company/pages/LeaveReview';
import { JobProvider } from '@company/state/jobContext';
import JobList from '@company/pages/JobList/JobList';
import { Header } from '@company/components/layout';
import {
  LoginRedirect,
  ThLoading,
  CompanyInactive,
} from '@components/elements';
import NotFound from '@components/elements/NotFound';
import { TEN_MINUTES } from '@constants/various';

export default function CompanyRoutes() {
  const isInIframe = window.self !== window.top;
  const [searchParams] = useSearchParams();
  const noHeader = searchParams.get('no-header') === 'true';
  const location = useLocation();

  const { fetchCompany, fetchNotifications, isLoading, isValidated } =
    useCompanyContext();

  useEffect(() => {
    async function fetchCompanyData() {
      await fetchCompany();
    }

    fetchCompanyData();
    fetchNotifications();
    const notificationsIntervalId = setInterval(
      fetchNotifications,
      2 * TEN_MINUTES
    );

    return () => {
      clearInterval(notificationsIntervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompany]);

  useEffect(() => {
    if (isProduction() && location?.pathname !== undefined) {
      googleManager.page({
        url: location.pathname,
      });
    }
  }, [location.pathname]);

  if (isLoading) {
    return <ThLoading />;
  }

  if (!isValidated) {
    return <CompanyInactive />;
  }

  return (
    <>
      {!(isInIframe || noHeader) && <Header />}
      <Routes>
        {isDev() && <Route path={`${BASE_PATH}`} element={<Dashboard />} />}

        <Route path={`${BASE_PATH}jobs`} element={<JobList />} />
        <Route path={`${BASE_PATH}login`} element={<LoginRedirect />} />
        <Route
          path={`${BASE_PATH}searchWorkers`}
          element={
            <UpdateWorkerProvider>
              <JobProvider>
                <JobAssignmentProvider>
                  <GeneralSearchTalent />
                </JobAssignmentProvider>
              </JobProvider>
            </UpdateWorkerProvider>
          }
        />
        <Route
          path={`${BASE_PATH}inbox-reviews/:activeTab?`}
          element={
            <JobProvider>
              <UpdateWorkerProvider>
                <JobAssignmentProvider>
                  <CommunicationHub />
                </JobAssignmentProvider>
              </UpdateWorkerProvider>
            </JobProvider>
          }
        />
        <Route
          path={`${BASE_PATH}payroll-services`}
          element={<PayrollServices isActive />}
        />
        <Route
          path={`${BASE_PATH}jobs/:id/:activeTab?`}
          element={
            <JobProvider>
              <JobShiftProvider>
                <JobWorkerShiftProvider>
                  <ActivityProvider>
                    <UpdateWorkerProvider>
                      <JobAssignmentProvider>
                        <JobOverview />
                      </JobAssignmentProvider>
                    </UpdateWorkerProvider>
                  </ActivityProvider>
                </JobWorkerShiftProvider>
              </JobShiftProvider>
            </JobProvider>
          }
        />

        <Route
          path={`${BASE_PATH}workers/:id`}
          element={
            <UpdateWorkerProvider>
              <WorkerProfilePage />
            </UpdateWorkerProvider>
          }
        />

        <Route
          path={`${BASE_PATH}leaveReview/:jobAssignmentId?`}
          element={<LeaveSingleReview isModal={isInIframe} />}
        />
        <Route
          path={`${BASE_PATH}batchReviews/:id?`}
          element={<LeaveReview />}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
